import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  createPoolShipping,
  getNewShipping,
  type ApiClient,
  type IframePoolOrderItem,
  type IframePoolShipping,
  type PoolShippingService,
  type ShippingQuota,
} from '../../../modules/api';
import {
  QUOTA_SUBMIT_REJECTED_MESSAGE,
  coversInsurance,
  isProtectionOptOut,
  useShippingFees,
} from '../../../modules/shipping';
import { buildPromoSummary } from '../../../modules/shippingPromo';
import { isLowBalanceLeftToHost } from '../../../modules/operatorLinks';
import { emit } from '../../../modules/postMessage';
import { usePayments } from '../../../modules/payments/usePayments';
import { useWallet } from '../../../modules/wallet/useWallet';
import { useDeliveryAddresses } from './address/useDeliveryAddresses';

// Shown when the fee can't be charged because the wallet balance is too low
// and no top-up completed (seamless casino balance, or a dismissed deposit
// screen). The backend returns error_code INSUFFICIENT_BALANCE for this.
const WALLET_BALANCE_TOO_LOW_MESSAGE =
  "Shipping couldn't be completed because your wallet balance is too low. " +
  'Top up your wallet to finalize your shipment.';

// Shown when the server would charge more than the total the player was shown
// (409 SHIPPING_PRICE_CHANGED). Nothing was charged; the page re-quotes.
const PRICE_CHANGED_MESSAGE = 'The delivery price has changed — please review the new total.';

export interface ShipmentDraftOptions {
  /** Pick the first service as soon as the catalogue loads (the shared page).
   *  Sleek leaves the choice to the player (partner Figma 2026-10-08). */
  preselectFirstService: boolean;
  /** Fired after `createPoolShipping` succeeds. */
  onCreated: (shipping: IframePoolShipping) => void;
}

/**
 * Everything a create-shipping screen needs besides its layout: the saved
 * addresses, the service catalogue and fulfilment quota, the live fee quote,
 * the required-protection rules (auto-select, opt-out acknowledgement) and
 * the submit with its Stripe top-up retry. Shared by the Delivery New page
 * and sleek's Ship Card page.
 */
export function useShipmentDraft(
  client: ApiClient,
  items: IframePoolOrderItem[],
  { preselectFirstService, onCreated }: ShipmentDraftOptions,
) {
  const addresses = useDeliveryAddresses(client);
  const address = addresses.selected;
  // Stripe deposit flow (no-op for seamless operators) + current balance, used
  // only to fund a shipping fee the player's internal balance can't cover.
  const { requestTopUp } = usePayments();
  const { balance_cents, refresh: refreshWallet } = useWallet();
  const [services, setServices] = useState<PoolShippingService[]>([]);
  // True until the /new catalogue settles (success or not) — lets a page hold
  // its layout back instead of growing when the options land.
  const [servicesLoading, setServicesLoading] = useState(true);
  // Per-player fulfilment-queue cap, quoted with the services. `null` until it
  // resolves (or on a backend that doesn't report one) → no cap is shown.
  const [quota, setQuota] = useState<ShippingQuota | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Player accepted the opt-out risk (checkbox). Reset whenever the chosen
  // option changes so a fresh opt-out always re-asks for consent.
  const [optOutAcknowledged, setOptOutAcknowledged] = useState(false);
  // Has the player manually picked an option? Once true we never auto-select
  // over their choice on later fee re-quotes.
  const userTouchedServiceRef = useRef(false);
  // We auto-select the required option only once (per required-tier value), so
  // a player who deliberately switches away isn't yanked back on every fees
  // re-quote.
  const autoSelectedForRef = useRef<string | null>(null);

  // Wrap the setter so any manual pick (radio click) is recorded as
  // user-touched and clears a stale opt-out acknowledgement.
  const selectService = (id: number) => {
    userTouchedServiceRef.current = true;
    setOptOutAcknowledged(false);
    setSelectedServiceId(id);
  };

  useEffect(() => {
    void getNewShipping(client).then((res) => {
      setServices(res.services);
      setQuota(res.quota ?? null);
      const first = res.services[0];
      // Pre-select the first option only when the player hasn't picked yet and
      // there's no auto-select target — protection-driven auto-select (below)
      // takes priority once the fees quote arrives.
      if (preselectFirstService && first && !userTouchedServiceRef.current) setSelectedServiceId(first.id);
    }).finally(() => setServicesLoading(false));
  }, [client, preselectFirstService]);

  // Backend-quoted figures: shipping = selected option's full EasyPost price,
  // handling = per-item flat, option_prices = full EasyPost price per option
  // for the radio list. The quote needs items + address, so all stay "TBD"
  // until both exist; re-quotes whenever the address, items or chosen option
  // change.
  const fees = useShippingFees(client, items.map((i) => i.id), {
    addressId: address?.id ?? null,
    serviceIds: selectedServiceId ? [selectedServiceId] : [],
  });

  // Required-protection summary, derived by the backend from the items alone —
  // so it arrives from the fees endpoint (quoted with pool_item_ids) even
  // before an address is picked. Drives auto-select, the "Required" badge, the
  // opt-out warning, and the high-value disclaimer.
  const protection = fees.fees?.protection ?? null;
  const requiredCodeName = protection?.requiredCodeName ?? null;

  // Auto-select the required option once it's known — but never over a manual
  // pick, and only once per required-tier value (so deliberately switching
  // away isn't undone on the next fee re-quote).
  useEffect(() => {
    if (!requiredCodeName) return;
    if (userTouchedServiceRef.current) return;
    if (autoSelectedForRef.current === requiredCodeName) return;
    const match = services.find((s) => s.codeName === requiredCodeName);
    if (!match) return;
    autoSelectedForRef.current = requiredCodeName;
    setSelectedServiceId(match.id);
  }, [requiredCodeName, services]);

  // The required tier changed (e.g. a pricier card was added) → the previous
  // opt-out consent no longer applies; re-ask for it.
  useEffect(() => {
    setOptOutAcknowledged(false);
  }, [requiredCodeName]);

  // `code_name` of the currently chosen option (from the /new catalogue).
  const chosenCodeName =
    services.find((s) => s.id === selectedServiceId)?.codeName ?? null;

  // Player picked weaker protection than required → opt-out. Gate Submit on a
  // checkbox acknowledgement and forward the flag to create.
  const isOptOut = isProtectionOptOut(protection, chosenCodeName);
  // A single card over the insurable cap → informational disclaimer (no gate).
  const showHighValueDisclaimer = protection?.singleCardOverMax ?? false;

  // No items selected → nothing to charge; force every row to "—" regardless
  // of any backend figure (handling in particular must not show a minimum for
  // an empty selection).
  const noItems = items.length === 0;
  const shippingFee = noItems ? null : fees.fees?.shippingFee ?? null;
  const handlingFee = noItems ? null : fees.fees?.handlingFee ?? null;
  // Backend split of the shipping fee (base / insurance / signature). When
  // present, the Shipping row shows carrier + signature and insurance gets its
  // own row; an older payload without the split keeps the full fee on one row.
  const feeComponents = noItems ? null : fees.fees?.shippingFeeComponents ?? null;
  const shippingRowValue = feeComponents
    ? Math.round((feeComponents.base + feeComponents.signature) * 100) / 100
    : shippingFee;
  // Insurance row only for an option that bundles insurance; its value stays
  // null ("—" / loader) until the quote arrives.
  const showInsuranceRow = coversInsurance(chosenCodeName);
  const insuranceRowValue = feeComponents?.insurance ?? null;
  // Sales tax only for operators that charge it (backend sends null/0
  // otherwise); a positive figure gets its own row and joins the total.
  const salesTaxQuoted = noItems ? null : fees.fees?.salesTax ?? null;
  const salesTax = salesTaxQuoted !== null && salesTaxQuoted > 0 ? salesTaxQuoted : null;
  // Total = sum of the charges we actually know; "—" until at least one is
  // quoted, so an all-unknown summary doesn't read as a confident $0.00.
  const knownFees = [shippingFee, handlingFee, salesTax].filter(
    (v): v is number => v !== null,
  );
  const total =
    knownFees.length > 0 ? knownFees.reduce((sum, v) => sum + v, 0) : null;
  // When a promotion priced the quote, every figure above is already what the
  // player pays (the total, and so the balance and shortfall checks, included);
  // this is the regular figure behind each row, to strike through.
  const promo = noItems
    ? null
    : buildPromoSummary(fees.fees?.promo, {
        shippingFee,
        handlingFee,
        salesTax,
        total,
        hasComponents: feeComponents !== null,
      });

  // Whole-dollar top-up that covers the gap between the fee total and the
  // player's current balance. The deposit screen is whole-dollar, so ceil the
  // shortfall to a dollar that always clears the fee. Used only by the Stripe
  // INSUFFICIENT_BALANCE retry below.
  const shortfallCents = (() => {
    if (total === null) return 0;
    const gap = Math.round(total * 100) - (balance_cents ?? 0);
    return gap > 0 ? Math.ceil(gap / 100) * 100 : 0;
  })();

  const formValid = address !== null && items.length > 0;
  // Opting out of a required protection requires an explicit acknowledgement
  // before we let the player submit (and before the backend would reject with
  // PROTECTION_ACK_REQUIRED).
  const protectionGate = !isOptOut || optOutAcknowledged;
  // The queue is full — nothing fits, so the form is replaced by a notice.
  const quotaFull = quota != null && quota.remaining === 0;
  // The basket outgrew the room left (items can also be seeded from My Packs,
  // i.e. picked before we knew the cap). Explain it and block submit rather
  // than let the backend refuse after the player filled in an address.
  const quotaOverflow = quota != null && !quotaFull && items.length > quota.remaining;
  // Block submit while the fee quote is in flight, during the POST, OR when the
  // live quote failed (`feesError`): submitting against an absent/stale/failed
  // quote risks charging the wrong amount — or just fails the charge.
  const canSubmit =
    formValid && protectionGate && !quotaOverflow && !fees.loading && !submitting && !fees.error;
  // Spinner only for an action the user is actually waiting on — the live quote
  // for a valid selection, or the submit POST itself. An invalid/empty form is
  // just disabled, no spinner.
  const submitLoading = submitting || (formValid && fees.loading);

  // The total to hold the server to: it refuses (and charges nothing) if the
  // price rose in between. The backend's own quoted total when the quote has
  // one — so a charge the page does not list can never make every submit a
  // false 409 — else the page's own sum. None while shipping is not quoted: a
  // partial sum is not a total the player can be charged.
  const quotedTotal = shippingFee === null ? null : (fees.fees?.total ?? total);
  const expectedTotal = quotedTotal === null ? null : Math.round(quotedTotal * 100) / 100;

  const handleSubmitError = (e: unknown) => {
    // Backend rejected the opt-out for lack of acknowledgement (422). Should
    // not happen given the client-side gate, but if the protection shifted
    // between quote and submit, surface a clear prompt instead of a raw HTTP
    // message and re-arm the checkbox.
    if (e instanceof ApiError && e.code === 'PROTECTION_ACK_REQUIRED') {
      setOptOutAcknowledged(false);
      setError('Please acknowledge the protection notice before submitting.');
    } else if (e instanceof ApiError && e.code === 'SHIPPING_PRICE_CHANGED') {
      // Stay on the page: re-quote so it shows the new total, and let the
      // player decide again at that price.
      setError(PRICE_CHANGED_MESSAGE);
      fees.retry();
    } else if (e instanceof ApiError && e.code === 'SHIPPING_ITEM_LIMIT_REACHED') {
      // The fulfilment queue filled up between opening the form and submitting
      // (another tab, or a delivery created elsewhere). The backend's message
      // carries the real numbers; fall back to the generic copy without them.
      setError(e.message || QUOTA_SUBMIT_REJECTED_MESSAGE);
      void getNewShipping(client).then((res) => setQuota(res.quota ?? null));
    } else {
      setError(e instanceof Error ? e.message : 'Failed to submit');
    }
  };

  // A shipping was created (HTTP 200) → the fee was debited from the wallet, so
  // refresh the balance. That fetch also emits `balance_updated` to the parent
  // platform, keeping the operator's own balance display in sync. Fire-and-
  // forget: `onCreated` may unmount the page, and the emit must not be gated
  // on the fetch settling.
  const handleShippingCreated = (shipping: IframePoolShipping) => {
    void refreshWallet();
    onCreated(shipping);
  };

  const submit = async () => {
    if (!canSubmit || !address) return;
    setSubmitting(true);
    setError(null);

    const params = {
      addressId: address.id,
      poolItemIds: items.map((i) => i.id),
      poolShippingServiceIds: selectedServiceId ? [selectedServiceId] : [],
      // Send the acknowledgement only when actually opting out; otherwise omit
      // it (no protection removed → nothing to acknowledge).
      ...(isOptOut ? { acknowledgedOptOut: optOutAcknowledged } : {}),
      ...(expectedTotal !== null ? { expectedTotal } : {}),
    };

    try {
      handleShippingCreated(await createPoolShipping(client, params));
    } catch (e) {
      // Stripe-funded player can't cover the fee from their internal balance →
      // open the deposit screen prefilled with the shortfall; once topped up,
      // retry the create once. requestTopUp resolves false (no-op) for
      // seamless operators, so this branch is inert there.
      if (e instanceof ApiError && e.code === 'INSUFFICIENT_BALANCE') {
        const funded = await requestTopUp(shortfallCents);
        if (funded) {
          try {
            handleShippingCreated(await createPoolShipping(client, params));
          } catch (retryErr) {
            handleSubmitError(retryErr);
          }
        } else if (isLowBalanceLeftToHost(e.code)) {
          // The operator hid our message: its site opens its own top-up popup
          // on this event instead.
          emit('shipping_failed', { code: e.code, message: WALLET_BALANCE_TOO_LOW_MESSAGE });
        } else {
          // No deposit screen (seamless casino balance) or the player dismissed
          // it — the wallet simply can't cover the fee. Prompt a top-up instead
          // of the raw operator decline message.
          setError(WALLET_BALANCE_TOO_LOW_MESSAGE);
        }
      } else {
        handleSubmitError(e);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    addresses,
    services,
    servicesLoading,
    quota,
    quotaFull,
    quotaOverflow,
    selectedServiceId,
    selectService,
    fees,
    requiredCodeName,
    isOptOut,
    optOutAcknowledged,
    setOptOutAcknowledged,
    showHighValueDisclaimer,
    summary: { shippingRowValue, showInsuranceRow, insuranceRowValue, handlingFee, salesTax, total, promo },
    canSubmit,
    submitLoading,
    error,
    submit,
  };
}
